/**
 * @fileoverview The recorder's one line about minutes (iOS review B6).
 *
 * The recorder sells nothing and warns with no banner. It shows what is
 * left, always, in one quiet line with a hairline meter where there is a
 * monthly allowance to measure against, and points at Plan & usage. A
 * mirror of `mobile/src/utils/recordingUsage.js`, on the shared meter model.
 */
import { buildUsageModel, formatMinutes } from "@/lib/usageMeter";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "42 of 180 min" under two hours; "7.5 of 10 hrs" from there, one unit for the pair. */
const pair = (left, total) => {
  if (total >= 120) return `${(left / 60).toFixed(1)} of ${(total / 60).toFixed(1)} hrs`;
  return `${Math.round(left)} of ${Math.round(total)} min`;
};

/**
 * @param {Object|null} usage - GET /subscription/usage.
 * @param {Date} [now]
 * @returns {{text: string, fraction: number|null, exhausted: boolean, monthly: boolean, resetsOn: string|null, org: string|null}|null}
 *   null until the payload arrives. `fraction` is what is left of the
 *   measurable total, for the meter; null when there is no denominator.
 */
export function recordingUsageLine(usage, now = new Date()) {
  if (!usage) return null;
  const { contexts } = buildUsageModel(usage);
  if (!contexts.length) return null;
  const personal = contexts.find((ctx) => ctx.key === "personal") || contexts[0];
  const month = MONTHS[now.getMonth()];
  const resetsOn = `${MONTHS[(now.getMonth() + 1) % 12]} 1`;
  const exhausted = contexts.every((ctx) => ctx.totalMinutes <= 0);

  // An instructor recording on an organization's hours watches that pool.
  const org = personal.totalMinutes <= 0 ? contexts.find((ctx) => ctx.key !== "personal" && ctx.totalMinutes > 0) : null;
  const ctx = org || personal;

  if (ctx.totalMinutes <= 0) {
    return { text: "No recording minutes left", fraction: 0, exhausted, monthly: !!ctx.monthly, resetsOn: ctx.monthly ? resetsOn : null, org: null };
  }
  const extra = ctx.pools.reduce((sum, pool) => sum + pool.minutes, 0);
  if (ctx.monthly) {
    const left = ctx.monthly.remainingMinutes + extra;
    const total = ctx.monthly.limitMinutes + extra;
    const where = org ? ` in ${org.label.replace(/ program hours$/, "")}’s hours` : "";
    return {
      text: `${pair(left, total)} left in ${month}${where}`,
      fraction: total > 0 ? Math.max(0, Math.min(1, left / total)) : null,
      exhausted,
      monthly: true,
      resetsOn,
      org: org ? org.label : null,
    };
  }
  return {
    text: `${formatMinutes(ctx.totalMinutes)} of recording left`,
    fraction: null,
    exhausted,
    monthly: false,
    resetsOn: null,
    org: org ? org.label : null,
  };
}
